/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 11th 2022, 7:01:23 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { OSFormInputProps, useControlledInput } from "ui-widgets/forms";
import { DesignLayout, LAYOUT_OPTIONS } from "lib/schemas";
import { getDefaultDesignLayout } from "lib/factories";
import { useGlobalScope } from "core/providers";
import { DesignLayoutEditor } from "./DesignLayoutEditor";

interface DesignLayoutMany2OneInlineInputProps<TPrimaryObject>
  extends Pick<OSFormInputProps<TPrimaryObject>, "schema_field_key">,
    Pick<React.ComponentProps<typeof DesignLayoutEditor>, "appendContents" | "setContents"> {
  layout_type: keyof typeof LAYOUT_OPTIONS;
}

export const DesignLayoutMany2OneInlineInput = <TPrimaryObject,>({
  schema_field_key,
  layout_type,
  appendContents,
  setContents,
}: DesignLayoutMany2OneInlineInputProps<TPrimaryObject>) => {
  const { designLayoutId, newDesignLayoutDefault, onAPISaveDesignLayout } = useDesignLayoutFormIput({
    schema_field_key,
    layout_type,
  });

  return (
    <DesignLayoutEditor
      layoutId={designLayoutId}
      newDesignLayoutDefault={newDesignLayoutDefault}
      onSaveDesignLayout={onAPISaveDesignLayout}
      appendContents={appendContents}
      setContents={setContents}
      minHeight={"300px"}
    />
  );
};

const useDesignLayoutFormIput = <TPrimaryObject,>({
  schema_field_key,
  layout_type,
}: DesignLayoutMany2OneInlineInputProps<TPrimaryObject>) => {
  const {
    field: { value: designLayoutId, onChange: updatePrimaryObject },
  } = useControlledInput<TPrimaryObject>({ schema_field_key });

  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const onAPISaveDesignLayout = (designLayout: DesignLayout) => designLayout.id && updatePrimaryObject(designLayout.id);

  const newDesignLayoutDefault = getDefaultDesignLayout({
    layout_type,
    organizationId: selectedOrganization?.id,
  });

  return {
    designLayoutId: designLayoutId as DesignLayout["id"],
    newDesignLayoutDefault,
    onAPISaveDesignLayout,
  };
};
